import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/shared/Common";
import { PageHero } from "@/components/shared/Visual";
import { Button } from "@/components/ui/button";
import { COMPANY } from "@/constants/testIds";
import { Star, ChevronRight, Sparkles } from "lucide-react";

export default function CompanyBookings() {
  const [bookings, setBookings] = useState([]);
  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);

  const awaitingReview = useMemo(
    () => bookings.filter((b) => b.status === "completed" && b.has_review === false),
    [bookings]
  );

  return (
    <div className="space-y-6" data-testid={COMPANY.bookingsList}>
      <PageHero eyebrow="Ops" title="Bookings" sub={`${bookings.length} total`} />

      {awaitingReview.length > 0 && (
        <div className="card-flat p-4 border-l-4 border-l-amber-400" data-testid="awaiting-review-banner">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-amber-50 border border-amber-100 flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-outfit font-semibold text-white">
                {awaitingReview.length} review{awaitingReview.length === 1 ? "" : "s"} pending
              </div>
              <p className="text-sm text-white/60 font-manrope">Reviews raise pros in rankings.</p>
              <div className="mt-3 flex flex-col gap-2">
                {awaitingReview.slice(0, 5).map((b) => (
                  <Link
                    key={b.id}
                    to={`/company/bookings/${b.id}#review`}
                    className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 hover:bg-amber-100 px-3 py-2 text-sm font-manrope"
                    data-testid={`awaiting-review-${b.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-outfit font-semibold text-white truncate">
                        {b.target_type === "professional" ? "Pro" : "Team"} · {b.date || "—"}
                      </div>
                      <div className="text-xs text-white/60">{b.workers}× · {b.hours}h @ €{b.hourly_rate}/hr</div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-brand font-outfit font-bold text-xs uppercase tracking-widest">
                      Review <ChevronRight className="w-3.5 h-3.5" />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {bookings.length === 0 && (
        <div className="card-flat p-8 text-center text-white/60 font-manrope" data-testid="company-bookings-empty">
          No bookings yet.
          <div className="mt-3">
            <Link to="/company/search">
              <Button variant="outline" size="sm">
                <Sparkles className="w-4 h-4 mr-1" /> Find security staff
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="grid gap-4">
        {bookings.map((b) => {
          const needsReview = b.status === "completed" && b.has_review === false;
          return (
            <Link
              key={b.id}
              to={`/company/bookings/${b.id}${needsReview ? "#review" : ""}`}
              className={`card-flat p-5 block hover:shadow-md ${needsReview ? "border-l-4 border-l-amber-400" : ""}`}
              data-testid={`booking-${b.id}`}
            >
              <div className="flex flex-wrap justify-between gap-3">
                <div>
                  <div className="font-outfit font-semibold text-white flex items-center gap-2">
                    {b.target_type === "professional" ? "Professional Booking" : "Team Booking"}
                    {needsReview && (
                      <span className="text-[10px] uppercase tracking-widest bg-amber-50 border border-amber-200 text-amber-700 rounded px-2 py-0.5 font-bold">
                        Review needed
                      </span>
                    )}
                    {b.has_review === true && (
                      <span className="text-[10px] uppercase tracking-widest bg-gold-50 border border-gold-200 text-gold-600 rounded px-2 py-0.5 font-bold">
                        Reviewed
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-white/60 font-manrope">{b.date || "—"} · {b.workers}× · {b.hours}h @ €{b.hourly_rate}/hr</div>
                  <div className="text-sm text-white/60 font-manrope">Fee: <span className="font-semibold">€{b.fee?.applied_fee}</span> ({b.fee?.fee_method})</div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  <StatusPill status={b.status} />
                  {b.payment_status !== "paid" && <span className="text-xs text-amber-600 font-manrope">Unpaid</span>}
                  {b.payment_status === "paid" && <span className="text-xs text-gold-500 font-manrope">Paid</span>}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
